import React from 'react';
import ContentPageTemplate from '@/components/ContentPageTemplate';
import SchemaMarkup from '@/components/SchemaMarkup';
import { buildFAQSchema } from '@/lib/schema';

// Single source of truth — drives both visible <FAQ> section AND
// FAQPage JSON-LD. All answers are grounded in facts already published
// elsewhere on this page (permit fees, timelines, HOA workflow). No
// invented numbers, no unsupported claims.
const FAQ_ITEMS = [
  {
    question: 'Do impact windows need a permit in Miami-Dade?',
    answer:
      'Yes. All impact window and door installations in Miami-Dade County — and across South Florida — require a building permit per the Florida Building Code. We prepare the plans, assemble the package, and submit the permit on your behalf as part of our $500 permit processing fee.',
  },
  {
    question: 'How long does permit approval usually take?',
    answer:
      "Permit timelines vary by municipality, typically a few days to several weeks. Most Miami-Dade cities approve standard residential impact window permits within 1–3 weeks. The final inspection is scheduled after installation and is usually completed within a few days to a week, depending on the city's availability.",
  },
  {
    question: 'Do condos or HOAs need approval before ordering windows?',
    answer:
      'Yes. Condo associations and HOAs require their own approval in addition to the city permit. We handle the HOA application, document submittal, and coordination with the board. Plan to add 2–4 weeks for HOA approval before your project can move forward.',
  },
  {
    question: 'Are city permit fees included in the quote?',
    answer:
      'No. Our $500 permit processing fee covers plan preparation, document packaging, submission, and HOA coordination. City permit fees (paid directly to the local building department) are separate and typically range from $150 to $1,000+ depending on project size and city.',
  },
  {
    question: 'Can installation start before the permit is approved?',
    answer:
      'No. Installation must wait until the city issues the permit. Starting work without an approved permit can result in stop-work orders, fines, and complications with insurance claims and resale. We schedule installation only after the permit is officially approved.',
  },
];

export default function PermitPage() {
  const pageData = {
    title: "Impact Window Permits in South Florida: Complete Guide 2026",
    metaDescription: "Everything you need to know about impact window permits in Miami-Dade and South Florida — requirements, fees, HOA approval, and timelines.",
    canonicalPath: "/impact-window-permits-miami-dade",
    
    directAnswer: (
      <>
        <p className="mb-3">
          <strong>Yes — all impact window and door installations in South Florida require a building permit</strong> in accordance with the Florida Building Code.
        </p>
        <p className="mb-3">
          We handle the entire permitting process for you, including plan preparation, document packaging, and submission to the city.
        </p>
        <p className="mb-3">
          <strong>Our permit processing fee is $500</strong>, which covers: preparation of required plans, complete permit package assembly, submission to the building department, and HOA coordination and document submission (if applicable).
        </p>
        <p className="mb-3">
          <strong>City permit fees are not included</strong> and typically range from $150 to $1,000+, depending on the size and scope of the project.
        </p>
        <p>
          Permit timelines vary depending on the municipality. Final inspections are scheduled after installation and are typically completed within a few days to a week, depending on the city's availability.
        </p>
      </>
    ),

    sections: [
      {
        title: "Do I Need a Permit in South Florida?",
        testId: "permit-requirement",
        content: (
          <p>
            Yes. South Florida municipalities require building permits for all impact window and door replacements, regardless of project size. This applies to:
          </p>
        ),
        bullets: [
          "Single window replacement (even just one window)",
          "Full-house window replacement",
          "Sliding glass door installation",
          "Entry door replacement with impact-rated doors",
          "Lanai or patio enclosure windows"
        ],
        highlight: "Permits protect you by ensuring installations meet current building codes and hurricane protection standards. Without permits, your insurance may not cover hurricane damage, and you may face issues when selling your home."
      },

      {
        title: "How Long Does the Permit Process Take?",
        testId: "permit-timeline",
        content: (
          <>
            <p className="mb-4">
              <strong>Permit timelines vary depending on the municipality.</strong>
            </p>
            <p className="mb-4">
              In some areas, permits can be approved in as little as 3–5 business days, while others may take 2–4 weeks depending on review times and requirements. Some municipalities offer expedited review windows, while others follow standard review timelines.
            </p>
            <p>
              Here's a typical permit timeline for most South Florida municipalities:
            </p>
          </>
        ),
        table: {
          headers: ["Phase", "Typical Timeline", "Details"],
          rows: [
            ["Application Submission", "1 day", "We submit plans, product NOAs, and contractor license"],
            ["Plan Review", "3–5 days to 2–4 weeks", "Municipality reviews for code compliance and HVHZ standards"],
            ["Approval/Issuance", "1–2 days", "Permit issued and ready for installation"],
            ["Installation", "1–3 days", "Professional installation by licensed contractor"],
            ["Final Inspection", "Few days to 1 week", "Inspector verifies code compliance based on city availability"],
            ["Permit Closure", "1–2 days", "Final approval and certificate of completion"]
          ]
        },
        highlight: "We coordinate every step so you don't have to. Total timeline varies by municipality — we'll provide specific timelines for your area when you request a quote."
      },

      {
        title: "What's Required for Permit Approval?",
        testId: "permit-requirements",
        content: (
          <>
            <p className="mb-3">
              Permit requirements vary depending on the municipality, but most projects require a combination of the following:
            </p>
            <ul className="list-disc pl-6 mb-4 space-y-1">
              <li>Completed permit application</li>
              <li>Product approvals (NOAs) confirming compliance with Florida Building Code / HVHZ</li>
              <li>Floor plans indicating window and door locations</li>
              <li>Window and door schedule</li>
              <li>HOA approval and/or HOA attestations (if applicable)</li>
              <li>Wind load calculations (required for certain projects)</li>
              <li>Property records (such as warranty deed or property appraiser information)</li>
              <li>Business documentation if the property is owned by an LLC (Sunbiz registration)</li>
              <li>Payment of city permit fees</li>
            </ul>
            <p className="mt-4">
              We prepare and submit all required documentation and coordinate directly with the building department to move the process forward. Additional documents may be requested by the city depending on the scope of work.
            </p>
          </>
        ),
        highlight: "Our $500 permit processing fee covers preparation of all required plans, complete permit package assembly, submission to the building department, and HOA coordination (if applicable)."
      },

      {
        title: "Step-by-Step Permit Process",
        testId: "permit-process",
        content: (
          <p>
            Here's exactly how we handle permits for you:
          </p>
        ),
        bullets: [
          <><strong>Step 1: Quote Acceptance</strong> — After you approve your quote, we gather property information and create installation plans</>,
          <><strong>Step 2: HOA Coordination (if needed)</strong> — We provide HOA-required documentation (specs, colors, NOAs) and assist with your application</>,
          <><strong>Step 3: Permit Submission</strong> — We submit complete application package to the local building department with all required documents</>,
          <><strong>Step 4: Review Process</strong> — County reviews for code compliance; we handle any questions or requested revisions</>,
          <><strong>Step 5: Permit Issuance</strong> — Once approved, permit is issued and we schedule your installation</>,
          <><strong>Step 6: Installation</strong> — Our licensed installers complete the work per approved plans</>,
          <><strong>Step 7: Final Inspection</strong> — County inspector verifies work meets code; we coordinate scheduling</>,
          <><strong>Step 8: Permit Closure</strong> — Final approval and certificate of completion provided to you for your records</>
        ]
      },

      {
        title: "HVHZ Compliance Requirements",
        testId: "hvhz-compliance",
        content: (
          <p>
            Much of South Florida is designated as a High-Velocity Hurricane Zone (HVHZ), which requires enhanced product standards:
          </p>
        ),
        bullets: [
          "Impact-rated windows must pass large missile impact testing (9-pound 2×4 at 50 ft/s)",
          "Products must meet Miami-Dade NOA (Notice of Acceptance) certification or Florida Product Approval",
          "Installation must follow manufacturer specifications exactly",
          "Fastening schedules must meet or exceed code requirements",
          "All products must display the Florida Product Approval number"
        ],
        highlight: "We only use HVHZ-certified products with valid certifications. Every installation meets or exceeds current code requirements for your specific municipality."
      },

      {
        title: "HOA Approval Process",
        testId: "hoa-considerations",
        content: (
          <p className="mb-3">
            If your property has an HOA, approval is often required <strong>before we can proceed with permitting or ordering materials.</strong>
          </p>
        ),
        bullets: [
          <><strong>We handle the entire HOA submission process</strong> including: contractor license and insurance, scope of work documentation, product approvals and supporting documents, and color/design specifications</>,
          <><strong>Timeline:</strong> HOA approval typically adds 2–4 weeks to overall project timeline depending on the association's review process</>,
          <><strong>Common HOA requirements:</strong> Exterior color matching, grid pattern approval, frame material specifications, and architectural compatibility review</>,
          <><strong>HOA coordination is included</strong> in our $500 permit processing fee — no additional charge for HOA document preparation and submission</>
        ],
        highlight: "We coordinate everything with your HOA so you don't have to. Start the HOA process early — we can provide documentation before your quote acceptance to save time."
      },

      {
        title: "Common Permit Delays and How to Avoid Them",
        testId: "permit-delays",
        content: (
          <p>
            Most delays are preventable with proper preparation:
          </p>
        ),
        table: {
          headers: ["Issue", "Delay", "How We Prevent It"],
          rows: [
            ["Missing NOA documentation", "1–2 weeks", "We pre-verify all product certifications"],
            ["Incomplete installation plans", "1 week", "Our plans are professionally prepared and complete"],
            ["HOA approval pending", "2–4 weeks", "We provide HOA docs early and assist with applications"],
            ["Code update compliance", "1 week", "We stay current with latest FBC and HVHZ requirements"],
            ["Inspection scheduling conflicts", "3–5 days", "We coordinate with municipality in advance"]
          ]
        }
      },

      {
        title: "Permit Fees & Processing",
        testId: "permit-fees",
        content: (
          <>
            <p className="mb-3">
              <strong>Our Permit Processing Fee: $500</strong>
            </p>
            <p className="mb-3">
              This covers all permit-related work including plan preparation, complete permit package assembly, submission to the local building department, HOA coordination and document submission (if applicable), and engineering coordination if required.
            </p>
            <p className="mb-2 mt-4">
              <strong>City Permit Fees (Paid Separately to Municipality):</strong>
            </p>
            <p className="mb-2">
              City permit fees vary based on project value and scope: Single window replacement ($150–$250), Partial replacement 5–10 windows ($200–$400), Full-house replacement 10+ windows ($350–$700), Large projects 20+ openings ($600–$1,000+).
            </p>
            <p className="mt-4">
              <strong>Additional Engineering (If Required):</strong> Some projects may require signed and sealed engineering plans or engineering letters for items such as concealed bucks and fasteners. These are priced separately based on project complexity.
            </p>
          </>
        ),
        highlight: "We handle all permit processing, submission, and coordination. City permit fees are paid directly to your local building department, and we collect and submit them on your behalf."
      },

      {
        title: "Frequently Asked Questions",
        testId: "faq",
        content: (
          <div className="space-y-6">
            {FAQ_ITEMS.map(({ question, answer }, i) => (
              <div key={i}>
                <p className="font-semibold text-[var(--brand-slate-900)] mb-2">{question}</p>
                <p className="text-[var(--brand-slate-700)]">{answer}</p>
              </div>
            ))}
          </div>
        ),
      }
    ],

    relatedLinks: [
      {
        title: "Impact Window Cost",
        description: "See complete pricing guide",
        url: "/impact-window-cost-miami"
      },
      {
        title: "Installation Process",
        description: "Learn our complete process",
        url: "/impact-window-installation-process"
      },
      {
        title: "Permit FAQ",
        description: "More permit questions answered",
        url: "/permits-impact-windows-miami-dade"
      },
      {
        title: "Miami Services",
        description: "City-specific information",
        url: "/impact-windows-miami"
      }
    ],

    ctaText: "We Handle All Permits For You",
    ctaSubtext: "Upload photos to start your project. We manage the entire permit process.",

    breadcrumbs: [
      { label: "Home", link: "/" },
      { label: "Permits South Florida" }
    ]
  };

  return (
    <>
      <SchemaMarkup
        schemas={buildFAQSchema(FAQ_ITEMS)}
        instanceId="permit-page-faq"
      />
      <ContentPageTemplate {...pageData} />
    </>
  );
}
